import { ScheduleTrans } from "../../utils/translations";
import { passCurrencySymbol } from "../../utils/currencyFormat";

export default function PriceCard({
  passTypes,
  language,
  selectedPass,
  setSelectedPass,
  setShowPurchaseModal,
  getActiveDiscount,
  translatePassName,
  setSelectedClass,
  setIsSubscription,
  className = "",
}) {
  const details = ScheduleTrans[language];

  const locale = language === "russian" ? "ru-RU" : "en-GB";
  const activeDiscounts = (passTypes || [])
    .map((pt) => getActiveDiscount(pt))
    .filter(Boolean);
  const discountValidUntil = (() => {
    const validToDates = activeDiscounts
      .map((d) => d?.validTo)
      .filter(Boolean)
      .map((v) => new Date(v))
      .filter((d) => !Number.isNaN(d.getTime()));
    if (!validToDates.length) return null;
    return new Date(Math.min(...validToDates.map((d) => d.getTime())));
  })();
  const discountValidUntilLabel =
    details.discountValidUntil || "Discounts valid until";
  const discountValidUntilText = discountValidUntil
    ? `${discountValidUntilLabel} ${discountValidUntil.toLocaleDateString(locale, {
        timeZone: "Asia/Jerusalem",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`
    : null;

  // Group passes by their section label (if any), preserving price order within
  // and across groups so a section's position follows its cheapest pass.
  const sortedPassTypes = [...passTypes].sort((a, b) => a.basePrice - b.basePrice);
  const sectionLabelOf = (passType) =>
    (language === "russian" ? passType.sectionLabelRu || passType.sectionLabel : passType.sectionLabel) || "";
  const groups = [];
  const groupIndexByLabel = new Map();
  sortedPassTypes.forEach((passType) => {
    const label = sectionLabelOf(passType);
    if (!groupIndexByLabel.has(label)) {
      groupIndexByLabel.set(label, groups.length);
      groups.push({ label, passTypes: [] });
    }
    groups[groupIndexByLabel.get(label)].passTypes.push(passType);
  });

  return (
    <div
      className={`flex flex-col items-center w-full sm:w-5/6 mx-auto ${className}`.trim()}
    >
      <div className="text-center md:text-lg text-base">{details.prices}</div>
      <div className="bg-beige rounded-3xl mt-5 w-full max-w-sm">
        <div className="bg-white m-4 p-4 md:p-7 rounded-3xl">
          {passTypes.length > 0 ? (
            <>
              {groups.map((group, groupIdx) => (
                <div key={group.label || `_ungrouped_${groupIdx}`}>
                  {group.label && (
                    <div className={`${groupIdx > 0 ? "mt-5" : "pb-1"}`}>
                      <h2 className="text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {group.label}
                      </h2>
                    </div>
                  )}
                  {group.passTypes.map((passType) => {
                    const activeDiscount = getActiveDiscount(passType);
                    const discountedPrice = activeDiscount
                      ? Math.round(passType.basePrice * (1 - activeDiscount.discountPercent / 100))
                      : passType.basePrice;
                    const curSym = passCurrencySymbol(passType.currency || "ILS");

                    return (
                      <div key={passType._id} className="text-center mb-3">
                        <h1 className="md:text-base text-sm font-medium border-b-[1px] border-lightGray">
                          {translatePassName(passType)}
                        </h1>
                        <div className="flex items-center justify-center gap-2 mt-1">
                          <p
                            className={`md:text-sm text-xs ${
                              activeDiscount ? "text-gray-400 line-through" : "text-gray-600"
                            }`}
                          >
                            {passType.basePrice} {curSym}
                          </p>
                          {activeDiscount && (
                            <p className="text-sm font-bold text-pink">
                              {discountedPrice} {curSym}
                            </p>
                          )}
                        </div>
                        <p className="md:text-sm text-xs text-gray-600">
                          {language === "russian" ? passType.descriptionRu : passType.descriptionEn}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ))}

              {discountValidUntilText && (
                <p className="mt-2 mb-4 text-center text-xs text-gray-400">
                  {discountValidUntilText}
                </p>
              )}

              <button
                className="w-fit mx-auto bg-brown text-white rounded-xl px-3 md:px-6 md:py-3 py-2 text-sm md:text-base font-semibold hover:bg-beige hover:text-brown transition"
                onClick={() => {
                  setShowPurchaseModal(true);
                  setIsSubscription(false);
                  setSelectedPass(sortedPassTypes[0] || passTypes[0]);
                  setSelectedClass(null);
                }}
                type="button"
              >
                {details.buyNow}
              </button>
            </>
          ) : (
            <p className="text-gray-500">{details.noPassesAvailable || "No passes available"}</p>
          )}
        </div>
      </div>
    </div>
  );
}
